import { Component, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { loadOrderDataAction, fillReturnOrderAction } from "../../../store/actions/order.actions";
import { clientConfig } from "../../../shared/constant/app.constant";
import { NavButton } from "../../../shared/components/navigation/next-button.component";
import { ReturnOrderService } from "../../../shared/services/return-order/return-order.service";
import { AppFlowService } from "../../../shared/services/app-flow/app-flow.service";
import { CopyHelpers } from "../../../shared/helpers/copy.helpers";
import { SpinnerService } from "../../../shared/services/spinner.service";
import { ExternalIntegrationsService } from "ClientApp/src/app/shared/services/externalIntegration.service";
import { getOrderDataFromShopifyOrder } from '../../../shared/mapper/shopify-order.mapper';
import { TenantOverrideService } from "../../../shared/services/tenant-overrides/tenant-override.service";
import { OverrideToggle } from "../../../shared/services/tenant-overrides/tenant-override.models";

@Component({
  selector: "retun-order-login",
  templateUrl: "./retun-order-login.component.html",
  styleUrls: ["./retun-order-login.component.scss"],
})
export class RetunOrderLoginComponent implements OnDestroy {
  brandInfo: BrandingInfo = {
    logoSrc: clientConfig.LOGO_URL,
    policyUrl: clientConfig.POLICYURL,
  };

  nextButton: NavButton = {
    buttonText: "Continue",
    routeUrl: "/d/main",
    cssClass: "next-btn",
  };
  orderNo: string;
  email: string;
  error: any = null;
  showError = false;
  hideInfo = false;
  showReturnChargeWarning = false;
  returnChargeWarningText: string | null = null;
  useExternalIntegration: boolean = clientConfig.USE_EXTERNAL_INTEGRATION;
  merchantId: string = clientConfig.MERCHANT_ID;
  tenantName = clientConfig.TENANTNAME;
  private readonly subscription: Subscription;

  constructor(
    private readonly router: Router,
    private readonly orderService: ReturnOrderService,
    private readonly appFlowSerivce: AppFlowService,
    private readonly store: Store<AppState>,
    private readonly spinnerService: SpinnerService,
    private readonly externalIntegrationsService: ExternalIntegrationsService,
    private readonly tenantOverrideService: TenantOverrideService
  ) {
    // Subscribe to tenant override changes - this will update when the HTTP call completes
    this.subscription = this.tenantOverrideService.overrideToggles.subscribe((toggles) => {
      this.showReturnChargeWarning = toggles.includes(OverrideToggle.ShowReturnChargeWarning);
      this.returnChargeWarningText = this.tenantOverrideService.getReturnChargeWarningText();
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  resetError() {
    this.error = null;
    return false;
  }

  async searchShipment(email: string, orderNo: string) {

    if (!email || !orderNo) {
      this.showError = true;
      return;
    }


    this.spinnerService.show();

    if (this.useExternalIntegration) {
      await this.searchOrder(orderNo, email, this.merchantId);
      this.spinnerService.hide();

      const seq = this.appFlowSerivce.findRouteFromSequence("login");
      this.router.navigate(["d", seq.next]);

      return;
    }

    this.orderService.searchShipment(email, orderNo).subscribe(
      (res: any) => {
        this.spinnerService.hide();

        if (res.isError) {
          this.handleErrorResponse(res.message);
          return;
        }

        if (res.success) {
          const order = res.order;
          if (!this.validateOrder(order)) {
            return;
          }

          const ord = CopyHelpers.deepCopy(order);

          if (ord.orders?.length) {

            let consolidatedOrders = ord.orders;

            if (this.tenantName.toLowerCase().trim() === 'dhl') {
              consolidatedOrders = this.consolidateOrderLines(ord.orders, res?.parcelCount);
              ord.orders = consolidatedOrders;
            }

            this.store.dispatch(
              loadOrderDataAction({ payload: { ...ord, isValid: true } })
            );

            this.store.dispatch(
              fillReturnOrderAction({
                payload: { order: consolidatedOrders[0] },
              })
            );
          }
          else {
            this.store.dispatch(
              loadOrderDataAction({ payload: { ...ord, isValid: true } })
            );
          }

          const seq = this.appFlowSerivce.findRouteFromSequence("login");
          this.router.navigate(["d", seq.next]);
        } else {
          if (res.error) console.error(res.error);
          this.handleErrorResponse("missingLoginOrder");
        }
      },
      () => {
        this.spinnerService.hide();
        this.handleErrorResponse("missingLoginOrder");
      }
    );
  }

  consolidateOrderLines(orders: any, parcelCount: number): any {
    this.orderService.parcelCount = parcelCount;

    const orderLines = orders[0]?.orderLines;

    if (!orderLines) return orders;

    const itemMap = new Map();

    for (const line of orderLines) {
      if (itemMap.has(line.itemNumber))
        itemMap.get(line.itemNumber).returnableQuantity += line.returnableQuantity || 0;
      else
        itemMap.set(line.itemNumber, { ...line });
    }

    const newOrders = [...orders];
    newOrders[0] = {
      ...orders[0],
      orderLines: Array.from(itemMap.values())
    };

    return newOrders;
  }

  private handleErrorResponse(message: string) {
    this.error = {
      title: "",
      message: message,
      translationParams: { merchantName: clientConfig.TITLE },
    };
  }

  private validateOrder(order) {
    if (!order.orders || order.orders.length === 0) {
      this.error = {
        title: "",
        message: "missingRequiredOrderInfo",
        translationParams: { merchantName: clientConfig.MERCHANTNAME },
      };
      return false;
    }

    const o = order.orders[0];
    if (
      !o.orderLines ||
      o.orderLines.length === 0 ||
      o.orderLines.filter((x) => !x.itemName || x.returnableQuantity < 1).length > 0
    ) {
      this.error = {
        title: "",
        message: "missingRequiredOrderInfo",
        translationParams: { merchantName: clientConfig.MERCHANTNAME },
      };
      return false;
    }

    return true;
  }

  private async searchOrder(orderNo: string, email: string, merchantId: string) {
    try {
      const res = await this.externalIntegrationsService.searchShopifyOrder(orderNo, email, merchantId).toPromise();
      this.externalIntegrationsService.shopifyOrderDetails = res;
      console.log(this.externalIntegrationsService.shopifyOrderDetails, "shopify");

      const shopifyOrder = res.data.orders.edges[0].node;

      const orderDataFromShopifyOrder = getOrderDataFromShopifyOrder(shopifyOrder, merchantId);

      this.store.dispatch(loadOrderDataAction({ payload: { ...orderDataFromShopifyOrder, isValid: true } }));

      const ord = CopyHelpers.deepCopy(orderDataFromShopifyOrder);

      if (ord.orders?.length) {
        this.store.dispatch(
          fillReturnOrderAction({
            payload: { order: orderDataFromShopifyOrder.orders[0] }
          })
        );
      }

      if (!this.externalIntegrationsService.shopifyOrderDetails) {
        this.handleErrorResponse("missingLoginOrder");
      }
    } catch (err) {
      console.warn('Error fetching Shopify order:', err);
      this.handleErrorResponse("missingLoginOrder");
    }
  }

}
